import React, { useEffect, useMemo, useState } from 'react';
import axios from '../services/axios';
import Modal from './Modal';

function InformeConsumoPanel({ zonas: zonasProp }) {
  const [zonasInternas, setZonasInternas] = useState([]);
  const [cargandoZonas, setCargandoZonas] = useState(false);
  const [zonaInformeConsumo, setZonaInformeConsumo] = useState('');
  const [mesInformeConsumo, setMesInformeConsumo] = useState(String(new Date().getMonth() + 1));
  const [anioInformeConsumo, setAnioInformeConsumo] = useState(String(new Date().getFullYear()));
  const [generandoInformeConsumo, setGenerandoInformeConsumo] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
  });

  const zonas = Array.isArray(zonasProp) ? zonasProp : zonasInternas;
  const zonasOrdenadas = useMemo(
    () => [...zonas].sort((a, b) => (a?.nombre || '').localeCompare(b?.nombre || '', 'es')),
    [zonas]
  );

  useEffect(() => {
    if (Array.isArray(zonasProp)) return;

    const cargarZonas = async () => {
      setCargandoZonas(true);
      try {
        const response = await axios.get('/zonas');
        setZonasInternas(response.data || []);
      } catch (error) {
        console.error('Error al cargar zonas para planilla de consumo:', error);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: 'No se pudieron cargar las zonas para generar la planilla de consumo.',
          onConfirm: () => setModal((prev) => ({ ...prev, isOpen: false })),
        });
      } finally {
        setCargandoZonas(false);
      }
    };

    cargarZonas();
  }, [zonasProp]);

  const cerrarModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  const descargarInformeConsumo = async () => {
    if (!zonaInformeConsumo) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Zona requerida',
        message: 'Seleccione una zona para generar la planilla de consumo.',
        onConfirm: cerrarModal,
      });
      return;
    }

    const mes = Number(mesInformeConsumo);
    const anio = Number(anioInformeConsumo);
    if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Mes inválido',
        message: 'Ingrese un mes válido entre 1 y 12.',
        onConfirm: cerrarModal,
      });
      return;
    }

    if (!Number.isInteger(anio) || anio < 2000 || anio > 2100) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Año inválido',
        message: 'Ingrese un año válido.',
        onConfirm: cerrarModal,
      });
      return;
    }

    setGenerandoInformeConsumo(true);
    try {
      const response = await axios.get(`/informe-consumo/zona/${zonaInformeConsumo}`, {
        params: { mes, anio },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const zonaNombre = zonasOrdenadas.find((zona) => zona.id === Number(zonaInformeConsumo))?.nombre || 'zona';

      link.href = url;
      link.setAttribute(
        'download',
        `planilla_consumo_${zonaNombre}_${String(mes).padStart(2, '0')}_${anio}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Planilla generada',
        message: `Se descargó la planilla de consumo para ${zonaNombre} (${String(mes).padStart(2, '0')}/${anio}).`,
        onConfirm: cerrarModal,
      });
    } catch (error) {
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error al generar planilla',
        message: error.response?.data?.message || 'No se pudo generar la planilla de consumo.',
        onConfirm: cerrarModal,
      });
    } finally {
      setGenerandoInformeConsumo(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-primary/10 rounded-lg p-3">
            <span className="material-symbols-outlined text-primary text-3xl">table_view</span>
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#111618]">Planilla de Consumo</h2>
            <p className="text-sm text-[#617c89] mt-1">
              Genere la planilla de consumo por zona y período.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zona</label>
              <select
                value={zonaInformeConsumo}
                onChange={(e) => setZonaInformeConsumo(e.target.value)}
                disabled={cargandoZonas || zonasOrdenadas.length === 0}
                className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0 disabled:opacity-60"
              >
                <option value="">
                  {cargandoZonas ? 'Cargando zonas...' : 'Seleccionar zona...'}
                </option>
                {zonasOrdenadas.map((zona) => (
                  <option key={zona.id} value={zona.id}>
                    {zona.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
              <input
                type="number"
                min="1"
                max="12"
                value={mesInformeConsumo}
                onChange={(e) => setMesInformeConsumo(e.target.value)}
                className="w-full h-12 px-4 rounded-lg bg-[#f0f3f4] border-none text-[#111618] focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={anioInformeConsumo}
                onChange={(e) => setAnioInformeConsumo(e.target.value)}
                className="w-full h-12 px-4 rounded-lg bg-[#f0f3f4] border-none text-[#111618] focus:outline-none focus:ring-0"
              />
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={descargarInformeConsumo}
              disabled={generandoInformeConsumo || !zonaInformeConsumo || cargandoZonas}
              className="h-12 px-5 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generandoInformeConsumo ? 'Generando PDF...' : 'Descargar Planilla de Consumo'}
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={cerrarModal}
      />
    </>
  );
}

export default InformeConsumoPanel;
